import { MapController } from '@deck.gl/core';
import DeckGL from '@deck.gl/react';
import WebMercatorViewport from '@math.gl/web-mercator';
import { type MouseEvent, memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  type ContextViewStateChangeInfo,
  _MapContext as MapContext,
  StaticMap,
} from 'react-map-gl-legacy';
import { TRANSITION_EVENTS } from 'react-map-gl-legacy';
import type { RectReadOnly as MeasureResult } from 'react-use-measure/types';

import mapboxgl from 'mapbox-gl-legacy';
import { type RenderInfoBuffer, getRenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import {
  MAPBOX_ACCESS_TOKEN,
  SHADOW_TRACK_STEP,
  type Tuple3,
  type WebGLContext,
} from 'src/lib/player/constants';
import { t } from 'src/lib/player/i18n';
import {
  getCurrentEvents,
  getEventExternalLayers,
  getEventStarters,
  getHiddenIds,
  getOptions,
  getSelectedIds,
  getVisibleEventPois,
  getVisibleEventTracks,
  getVisibleSelectedPois,
  getVisibleSelectedStarters,
  getVisibleSelectedTracks,
} from 'src/lib/player/selectors';
import {
  type LatLngOpacity,
  type PoiType,
  type StarterType,
  type State,
  type TrackType,
  useStore,
} from 'src/lib/player/store';
import { flatMap, getBoundsFromCoordinates } from 'src/lib/player/utils';
import type TrackView from 'src/lib/player/utils/TrackView';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import FullscreenControl from './Map/FullscreenControl';
import NavigationControl from './Map/NavigationControl';
import OwnLocationControl from './Map/OwnLocationControl';
import { createPOILayer } from './Map/POILayer';
import ScaleControl from './Map/ScaleControl';
import createStarterMarkerLayer from './Map/StarterMarkerLayer';
import createStarterParticleLayer from './Map/StarterParticleLayer';
import createStarterTrackLayer from './Map/StarterTrackLayer';
import createTrackHoverLayer from './Map/TrackHoverLayer';
import { useMapStyle } from './Map/loadMapStyle';

const FAST_ANIMATION_DURATION = 250;

mapboxgl.setRTLTextPlugin(
  'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
  null,
  true, // Lazy load the plugin
);

type Viewport = {
  width: number;
  height: number;
  latitude: number;
  longitude: number;
  zoom: number;
  pitch?: number;
  bearing?: number;
  transitionInterruption?: typeof TRANSITION_EVENTS;
};

export type PointerEvent = MouseEvent & {
  center: {
    x: number;
    y: number;
  };
};

function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function translateViewport(
  viewport: Viewport,
  source: MeasureResult,
  target: MeasureResult,
): Viewport {
  const mercatorProjection = new WebMercatorViewport(viewport);

  const targetCenter = [target.left + target.width / 2, target.top + target.height / 2];
  const targetCenterRelativeToSource = [
    targetCenter[0] - source.left,
    targetCenter[1] - source.top,
  ];

  const centerLngLat = mercatorProjection.unproject(targetCenterRelativeToSource);

  return {
    bearing: 0,
    pitch: 0,
    ...viewport,
    longitude: centerLngLat[0],
    latitude: centerLngLat[1],
    width: target.width,
    height: target.height,
  };
}

function fitBounds(coordinates: Array<Tuple3>, viewport: Viewport, padding = 0.2) {
  if (coordinates.length === 0) {
    return null;
  }
  const bounds = getBoundsFromCoordinates(coordinates);
  const { longitude, latitude, zoom } = new WebMercatorViewport(viewport).fitBounds(
    [
      [bounds.min_lng, bounds.min_lat],
      [bounds.max_lng, bounds.max_lat],
    ],
    {
      padding: {
        top: viewport.height * padding,
        bottom: viewport.height * padding,
        left: viewport.width * padding,
        right: viewport.width * padding,
      },
    },
  );
  return {
    ...viewport,
    latitude,
    longitude,
    zoom,
  };
}

function areSelectedInView(
  selectedStarters: Array<StarterType>,
  selectedPois: Array<PoiType>,
  selectedTracks: Array<TrackType>,
  ownLocation: LatLngOpacity | null,
  visibleViewport: Viewport,
  renderInfoBuffer: RenderInfoBuffer,
  padding = 0,
): [boolean, Array<Tuple3>] {
  if (
    selectedStarters.length === 0 &&
    selectedPois.length === 0 &&
    selectedTracks.length === 0 &&
    ownLocation == null
  ) {
    return [false, []];
  }
  const proj = new WebMercatorViewport(visibleViewport);
  let selectedCoordinates: Array<Tuple3> = selectedStarters
    .map((starter) =>
      renderInfoBuffer.getLatLngOpacity(
        renderInfoBuffer.getRenderIndex(starter.eventIndex, starter.index),
      ),
    )
    .filter((latlng) => latlng.opacity > 0)
    .map((latlng) => [latlng.lng, latlng.lat, 0]);

  if (ownLocation != null) {
    selectedCoordinates.push([ownLocation.lng, ownLocation.lat, 0]);
  }

  for (const poi of selectedPois) {
    selectedCoordinates.push([poi.latlng.lng, poi.latlng.lat, 0]);
  }
  for (const track of selectedTracks) {
    selectedCoordinates = selectedCoordinates.concat(track.coordinates);
  }

  const allInView = selectedCoordinates.every((coordinate) => {
    const [x, y] = proj.project(coordinate);
    return (
      padding * proj.width < x &&
      x < (1 - padding) * proj.width &&
      padding * proj.height < y &&
      y < (1 - padding) * proj.height
    );
  });
  return [allInView, selectedCoordinates];
}

const timeSelector = (state: State) => state.timer.time;
const allEventsSelector = (state: State) => state.events;
const ownLocationSelector = (state: State) => state.timer.geolocation.latlng;
const highlightedLocationSelector = (state: State) => {
  const highlightedOffset = state.timer.highlightedOffset;
  if (highlightedOffset >= 0) {
    return state.events[state.currentEventIndices[0]]?.chunkedShadowTrack?.getLngLatInterpolated(
      highlightedOffset / SHADOW_TRACK_STEP,
    );
  }
  return null;
};
const starterTrackInfoSelector = (state: State) =>
  state.currentEventIndices
    .filter((i) => state.eventsChangeable[i].starterTracks.size > 0)
    .map((i) => ({
      tracks: Array.from(state.eventsChangeable[i].starterTracks.entries()).filter(
        ([, points]) => points.length > 0,
      ),
      tailTimeout: state.events[i].options.tailTimeout,
      markerTimeout: state.events[i].options.markerTimeout,
    }));

function MapComponent({
  visibleMapBounds,
  mapBounds,
}: {
  visibleMapBounds: MeasureResult;
  mapBounds: MeasureResult;
}) {
  const { width, height } = mapBounds;
  const toggleSelectedStarter = useStore((state) => state.actions.toggleSelectedStarter);
  const setHighlighted = useStore((state) => state.actions.setHighlighted);

  const currentViewStateRef = useRef<ContextViewStateChangeInfo>(null);
  const isOurTransitionRef = useRef(false);
  const [viewport, setViewport] = useState<Viewport>(() => ({
    width,
    height,
    latitude: 0,
    longitude: 0,
    zoom: 0,
  }));

  const [mapIsLoaded, setMapIsLoaded] = useState(false);
  const [shouldFollow, setShouldFollow] = useState(true);
  const [isPitched, setIsPitched] = useState(false);
  const glRef = useRef<WebGLContext>();

  const allEvents = useStore(allEventsSelector);
  const currentEvents = useStore(getCurrentEvents);
  // Distance markers may be shown, even for hidden shadowtracks
  const shadowTracks = useMemo(
    () =>
      currentEvents
        .map((event) => [event.chunkedShadowTrack, event.options.showDistanceMarkers])
        .filter(([track]) => track != null),
    [currentEvents],
  ) as Array<[TrackView, boolean]>;
  const ownLocation = useStore(ownLocationSelector);
  const options = useStore(getOptions);
  const selectedStarters = useStore(getVisibleSelectedStarters);
  const selectedPois = useStore(getVisibleSelectedPois);
  const selectedTracks = useStore(getVisibleSelectedTracks);
  const selectedIds = useStore((state) => getSelectedIds(state.timer));
  const hiddenIds = useStore((state) => getHiddenIds(state.timer));
  const highlightedLocation = useStore(highlightedLocationSelector);
  const starters = getEventStarters(currentEvents);
  const pois = getVisibleEventPois(currentEvents, hiddenIds);
  const tracks = getVisibleEventTracks(currentEvents, hiddenIds);
  const externalLayers = getEventExternalLayers(currentEvents);
  const starterTrackInfo = useStore(starterTrackInfoSelector);
  const time = useStore(timeSelector);

  const tailTimeout = currentEvents.reduce((r, a) => Math.max(r, a.options.tailTimeout), -1);

  const renderInfoBuffer = useStore(getRenderInfoBuffer);
  const mapStyle = useMapStyle(
    options.mapType,
    tracks,
    externalLayers,
    shadowTracks,
    selectedIds,
    options.unitType,
    options.largeMode,
  );

  const isReady = mapStyle != null;

  function handleMarkerClick(starter: StarterType) {
    toggleSelectedStarter(starter.id);
  }

  function handleChangeViewState(args: ContextViewStateChangeInfo) {
    currentViewStateRef.current = args;
    const visibleViewport = translateViewport(args.viewState, mapBounds, visibleMapBounds);
    if (
      (args.interactionState.isDragging || args.interactionState.isZooming) &&
      !areSelectedInView(
        selectedStarters,
        selectedPois,
        selectedTracks,
        ownLocation,
        visibleViewport,
        renderInfoBuffer,
      )[0]
    ) {
      setShouldFollow(false);
    }
    setIsPitched(args.viewState.pitch !== 0);
    setViewport(args.viewState);
  }

  function handleNavigationControlChange(viewState: Partial<Viewport>) {
    const currentViewState = currentViewStateRef.current?.viewState ?? viewport;
    setViewport({
      ...currentViewState,
      ...viewState,
      transitionDuration: FAST_ANIMATION_DURATION,
      transitionEasing: easeInOut,
      transitionInterruption: TRANSITION_EVENTS.BREAK,
    });
  }

  // Remove Mapbox logo
  useEffect(() => {
    const mapboxLogoEl = document.querySelector(
      '.mapboxgl-ctrl-bottom-left .mapboxgl-ctrl-logo',
    ) as HTMLAnchorElement;
    if (mapboxLogoEl != null) {
      mapboxLogoEl.parentNode?.removeChild(mapboxLogoEl);
    }
  }, [mapIsLoaded]);

  useEffect(() => {
    const visibleCoordinates = pois
      .map((poi) => [poi.latlng.lng, poi.latlng.lat, 0] as Tuple3)
      .concat(flatMap(tracks, (track) => track.coordinates));
    if (
      (currentEvents.length === 1 && currentEvents[0].options.viewport != null) ||
      (visibleCoordinates.length === 0 && currentEvents.some((ev) => ev.options.viewport != null))
    ) {
      // Apply pre-defined viewport
      const eventViewport = currentEvents.find((ev) => ev.options.viewport != null)?.options
        .viewport;

      let newViewport: Viewport = {
        ...eventViewport,
        zoom: Math.log2(visibleMapBounds.width / (1000 / 2 ** eventViewport.zoom)),
        width: visibleMapBounds.width,
        height: visibleMapBounds.height,
      };
      if (options.overrideZoom != null) {
        newViewport.zoom = options.overrideZoom;
      }
      newViewport = translateViewport(newViewport, visibleMapBounds, mapBounds);
      setViewport({
        ...newViewport,
        transitionInterruption: TRANSITION_EVENTS.SNAP_TO_END,
      });
      currentViewStateRef.current = {
        viewState: newViewport,
        interactionState: {
          inTransition: false,
          isDragging: false,
          isPanning: false,
          isRotating: false,
          isZooming: false,
        },
        oldViewState: newViewport,
      };
    } else if (visibleCoordinates.length > 0) {
      // Center map around geo features initially
      const visibleViewport = translateViewport(
        {
          ...viewport,
          width,
          height,
        },
        mapBounds,
        visibleMapBounds,
      );
      let newViewport = fitBounds(visibleCoordinates, visibleViewport);
      if (options.overrideZoom != null) {
        newViewport.zoom = options.overrideZoom;
      }
      newViewport = translateViewport(newViewport, visibleMapBounds, mapBounds);
      setViewport({
        ...newViewport,
        transitionInterruption: TRANSITION_EVENTS.SNAP_TO_END,
      });
      currentViewStateRef.current = {
        viewState: newViewport,
        interactionState: {
          inTransition: false,
          isDragging: false,
          isPanning: false,
          isRotating: false,
          isZooming: false,
        },
        oldViewState: newViewport,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pois, tracks, currentEvents]);

  // Keep selected starters in view
  useEffect(() => {
    const currentViewState = currentViewStateRef.current;
    if (currentViewState != null) {
      const currentViewport = currentViewState.viewState;
      const visibleViewport = translateViewport(currentViewport, mapBounds, visibleMapBounds);

      const [allInView, selectedCoordinates] = areSelectedInView(
        selectedStarters,
        selectedPois,
        selectedTracks,
        ownLocation,
        visibleViewport,
        renderInfoBuffer,
        0.1,
      );

      const isInteracting =
        !shouldFollow || currentViewState.interactionState.isDragging || isOurTransitionRef.current;

      if (
        !allInView &&
        !isInteracting &&
        visibleMapBounds.width > 100 &&
        visibleMapBounds.height > 100
      ) {
        if (selectedCoordinates.length === 1) {
          isOurTransitionRef.current = true && mapIsLoaded;
          const translatedLngLat = translateViewport(
            {
              ...visibleViewport,
              longitude: selectedCoordinates[0][0],
              latitude: selectedCoordinates[0][1],
            },
            visibleMapBounds,
            mapBounds,
          );
          setViewport({
            ...currentViewport,
            latitude: translatedLngLat.latitude,
            longitude: translatedLngLat.longitude,
            transitionDuration: FAST_ANIMATION_DURATION,
            transitionEasing: easeInOut,
            transitionInterruption: TRANSITION_EVENTS.BREAK,
            onTransitionEnd: () => {
              isOurTransitionRef.current = false;
            },
            onTransitionInterrupt: () => {
              isOurTransitionRef.current = false;
            },
          });
        } else if (selectedCoordinates.length > 1) {
          const fittedViewport = fitBounds(selectedCoordinates, visibleViewport);
          const translatedFittedViewport = translateViewport(
            { ...fittedViewport, zoom: Math.min(currentViewport.zoom, fittedViewport.zoom) },
            visibleMapBounds,
            mapBounds,
          );
          isOurTransitionRef.current = true && mapIsLoaded;
          setViewport({
            ...currentViewport,
            latitude: translatedFittedViewport.latitude,
            longitude: translatedFittedViewport.longitude,
            zoom: translatedFittedViewport.zoom,
            transitionDuration: FAST_ANIMATION_DURATION,
            transitionEasing: easeInOut,
            transitionInterruption: TRANSITION_EVENTS.BREAK,
            onTransitionEnd: () => {
              isOurTransitionRef.current = false;
            },
            onTransitionInterrupt: () => {
              isOurTransitionRef.current = false;
            },
          });
        }
      }
    }
  }, [
    mapIsLoaded,
    renderInfoBuffer,
    ownLocation,
    viewport,
    visibleMapBounds,
    mapBounds,
    selectedStarters,
    selectedPois,
    selectedTracks,
    shouldFollow,
  ]);

  const layers = [];
  if (glRef.current != null) {
    // Draw starter tails
    if (tailTimeout !== 0) {
      layers.push(
        ...starterTrackInfo.map((info) =>
          createStarterTrackLayer(
            info.tracks,
            starters,
            renderInfoBuffer,
            time,
            info.tailTimeout,
            info.markerTimeout,
          ),
        ),
      );
    }
    // Draw starter dots
    layers.push(
      createStarterParticleLayer({
        renderInfoBuffer,
        gl: glRef.current,
        onClick: (starterIndex) => handleMarkerClick(starters[starterIndex]),
        onHover: (starterIndex) => setHighlighted(starters[starterIndex]?.id),
      }),
    );
    // Draw text-based POI
    layers.push(
      ...createPOILayer({
        pois,
        ownLocation,
        selectedIds,
        gl: glRef.current,
        largeMode: options.largeMode,
        onClick: (poi) => toggleSelectedStarter(poi.id),
        onHover: (poi) => setHighlighted(poi?.id),
      }),
    );
    // Draw selected starter flags
    layers.push(
      ...createStarterMarkerLayer({
        renderInfoBuffer,
        starters,
        gl: glRef.current,
        selectedIds,
        hiddenIds,
        flagContentByEventIndex: allEvents.map((ev) => ev.options.flagContent),
        selectedFlagContentByEventIndex: allEvents.map((ev) => ev.options.selectedFlagContent),
        overrideFlagContent: options.overrideFlagContent,
        overrideSelectedFlagContent: options.overrideSelectedFlagContent,
        largeMode: options.largeMode,
        onClick: (starter) => handleMarkerClick(starter),
        onHover: (starter) => setHighlighted(starter?.id),
      }),
    );

    layers.push(createTrackHoverLayer(highlightedLocation));
  }

  const renderViewport = {
    ...viewport,
    width,
    height,
  };

  return (
    <div className="map">
      <div style={{ position: 'absolute', top: 0, left: 0 }}>
        {isReady && (
          <DeckGL
            onWebGLInitialized={(gl: WebGLContext) => {
              glRef.current = gl;
            }}
            controller={{ type: MapController, touchRotate: true }}
            width={renderViewport.width}
            height={renderViewport.height}
            initialViewState={renderViewport}
            ContextProvider={MapContext.Provider}
            onViewStateChange={handleChangeViewState}
            layers={layers}
          >
            <StaticMap
              key="static-map"
              mapboxApiAccessToken={MAPBOX_ACCESS_TOKEN}
              mapStyle={mapStyle}
              reuseMaps={false}
              onLoad={() => setMapIsLoaded(true)}
            />
            <div key="map-ctrl-container" className="map-ctrl-container">
              {(selectedStarters.length > 0 ||
                selectedPois.length > 0 ||
                selectedTracks.length > 0 ||
                ownLocation != null) &&
                !shouldFollow && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 41,
                      zIndex: 10,
                    }}
                  >
                    <div className="mapboxgl-ctrl-group">
                      <button
                        className="mapboxgl-ctrl-icon follow-selected-button"
                        title={t('map.followSelected')}
                        {...onClickGenerator(() => setShouldFollow(true))}
                      >
                        <i className="fas fa-thumbtack" /> {t('map.followSelected')}
                      </button>
                    </div>
                  </div>
                )}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  zIndex: 10,
                }}
              >
                <NavigationControl
                  bearing={viewport.bearing ?? 0}
                  onZoomIn={() => {
                    const currentViewState = currentViewStateRef.current?.viewState ?? viewport;
                    handleNavigationControlChange({ zoom: (currentViewState.zoom ?? 0) + 1 });
                  }}
                  onZoomOut={() => {
                    const currentViewState = currentViewStateRef.current?.viewState ?? viewport;
                    handleNavigationControlChange({ zoom: (currentViewState.zoom ?? 0) - 1 });
                  }}
                  onResetNorth={() => {
                    handleNavigationControlChange({ bearing: 0, pitch: 0 });
                  }}
                />
                <div style={{ height: 10 }} />
                <OwnLocationControl />
                {options.fullscreenButton && (
                  <>
                    <div style={{ height: 10 }} />
                    <FullscreenControl />
                  </>
                )}
              </div>
              <div
                style={{
                  position: 'absolute',
                  top: visibleMapBounds.height - 50,
                  right: 0,
                  zIndex: 10,
                }}
              >
                {!isPitched && (
                  <ScaleControl
                    latitude={viewport.latitude}
                    zoom={viewport.zoom}
                    style={{ bottom: 0, right: 0 }}
                    unit={options.unitType.toLowerCase() as 'metric' | 'imperial' | 'nautical'}
                  />
                )}
              </div>
            </div>
          </DeckGL>
        )}
      </div>
    </div>
  );
}

export default memo(MapComponent);
